import React from 'react';

// Shared with effy-fe's mgsa (/w) and analytics (/a), which run on this origin.
// Keep in sync with `SIDEBAR_COLLAPSED_STORAGE_KEY` in effy-fe's `@lyearn/roomba`.
const STORAGE_KEY = 'lyearn:sidebar-collapsed';
// Read only while STORAGE_KEY has no value yet, then removed.
const LEGACY_STORAGE_KEY = 'academy:sidebar-collapsed';

interface SidebarCollapseValue {
  isCollapsed: boolean;
  toggle: () => void;
  isMobileNavOpen: boolean;
  openMobileNav: () => void;
  closeMobileNav: () => void;
}

const SidebarCollapseContext = React.createContext<SidebarCollapseValue>({
  isCollapsed: false,
  toggle: () => undefined,
  isMobileNavOpen: false,
  openMobileNav: () => undefined,
  closeMobileNav: () => undefined,
});

const readStored = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    const stored =
      window.localStorage.getItem(STORAGE_KEY) ?? window.localStorage.getItem(LEGACY_STORAGE_KEY);
    return stored === '1';
  } catch {
    return false;
  }
};

export const SidebarCollapseProvider = ({ children }: React.PropsWithChildren) => {
  // Mounted under AppBody's SafeHydrate, so this first render is client-only and
  // can start from the stored value instead of animating to it after mount.
  const [isCollapsed, setIsCollapsed] = React.useState(readStored);
  const [isMobileNavOpen, setIsMobileNavOpen] = React.useState(false);

  React.useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, isCollapsed ? '1' : '0');
      window.localStorage.removeItem(LEGACY_STORAGE_KEY);
    } catch {}
  }, [isCollapsed]);

  // Follow toggles from other tabs, including the effy-fe apps sharing the key.
  React.useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key === STORAGE_KEY && event.newValue !== null) {
        setIsCollapsed(event.newValue === '1');
      }
    };

    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const toggle = React.useCallback(() => setIsCollapsed((collapsed) => !collapsed), []);

  const openMobileNav = React.useCallback(() => setIsMobileNavOpen(true), []);
  const closeMobileNav = React.useCallback(() => setIsMobileNavOpen(false), []);

  const value = React.useMemo(
    () => ({ isCollapsed, toggle, isMobileNavOpen, openMobileNav, closeMobileNav }),
    [isCollapsed, toggle, isMobileNavOpen, openMobileNav, closeMobileNav],
  );

  return (
    <SidebarCollapseContext.Provider value={value}>{children}</SidebarCollapseContext.Provider>
  );
};

export const useSidebarCollapse = () => React.useContext(SidebarCollapseContext);
